import type { Options as HighchartsOptions } from 'highcharts';

export interface ModelPricing {
    request: number;
    response: number;
}

export interface TableDataRow {
    id: number;
    application: string;
    tokenUsage: number;
    estimationCost: number;
}

// Model pricing (per million tokens)
const modelUnitCost: Record<string, [number, number]> = {
    GPT4O: [5, 15],
    GPT5: [7, 21],
    O3: [1.1, 4.4],
    CLAUDE35: [3, 15],
    CLAUDE37: [3, 15],
    CLAUDE40: [3, 15],
    CLAUDE45: [3, 15],
    CLAUDE45_AI_FOUNDRY: [3, 15],
};

// Application title mappings
export const titleMap: Record<string, string> = {
    appkit_internal: 'Appkit Internal Use',
    Copilot: 'Appkit Assist',
    AccChecker: 'Accessibility (Plugin)',
    'rapid-craft': 'Rapid Craft',
    All: 'All Application',
};

// Application to color mappings for charts
export const applicationToColorMap: Record<string, string> = {
    vscode: '#26776D',
    Copilot: '#D04A02',
    AccCheckEX: '#D93954',
    AccChecker: '#415385',
};

// Application to table column index mappings
export const appToTableColumnMap: Record<string, number> = {
    vscode: 0,
    Copilot: 1,
    AccCheckEX: 2,
    AccChecker: 3,
    All: 4,
};

// NGC (NVIDIA GPU Cloud) extra cost coefficient
export const NGC_EXTRA_COST_COEFFICIENT = 1.2;

/**
 * Get completion request unit cost for a given model
 * @param modelName - Name of the AI model
 * @returns Cost per token (in dollars)
 */
export const COMPLETION_REQ_UNIT_COST = (modelName: string): number => {
    if (!modelUnitCost[modelName]) return 0;
    return (modelUnitCost[modelName][0] / 1000000) * NGC_EXTRA_COST_COEFFICIENT;
};

/**
 * Get completion response unit cost for a given model
 * @param modelName - Name of the AI model
 * @returns Cost per token (in dollars)
 */
export const COMPLETION_RES_UNIT_COST = (modelName: string): number => {
    if (!modelUnitCost[modelName]) return 0;
    return (modelUnitCost[modelName][1] / 1000000) * NGC_EXTRA_COST_COEFFICIENT;
};

// Highcharts options for daily usage chart
export const dailyUsageOptions: HighchartsOptions = {
    chart: {
        type: 'column',
    },
    title: {
        text: 'Daily Token Usage',
        align: 'center',
    },
    xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
            month: '%e. %b',
            year: '%b',
        },
        title: {
            text: '',
        },
    },
    yAxis: {
        min: 0,
        title: {
            text: 'Token',
        },
    },
    tooltip: {
        valueSuffix: '',
    },
    plotOptions: {
        column: {
            pointPadding: 0.2,
            borderWidth: 0,
        },
    },
    series: [],
};

// Highcharts options for distribution pie chart
export const distributionPieOptions: HighchartsOptions = {
    chart: {
        type: 'pie',
    },
    title: {
        text: 'Token Usage Distribution',
    },
    tooltip: {
        valueSuffix: '%',
    },
    plotOptions: {
        series: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: [
                {
                    enabled: true,
                    distance: 20,
                } as any,
                {
                    enabled: true,
                    style: {
                        fontSize: '1.2em',
                        textOutline: 'none',
                        opacity: 1,
                        color: '#000',
                    },
                    filter: {
                        property: 'name',
                        value: 10,
                    },
                },
            ],
        },
    },
    series: [
        {
            type: 'pie',
            name: 'Percentage',
            colorByPoint: true,
            data: [],
        } as any,
    ],
};

// Highcharts options for daily cost estimation chart
export const dailyCostEstimationOptions: HighchartsOptions = {
    title: {
        text: 'Daily Cost Estimation',
    },
    chart: {
        type: 'column',
    },
    tooltip: {
        valuePrefix: '$',
    },
    xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
            month: '%e. %b',
            year: '%b',
        },
        title: {
            text: 'Date',
        },
    },
    yAxis: {
        title: {
            text: 'Cost ($)',
        },
        min: 0,
    },
    series: [],
};

// Initial table data structure
export const initialTableData: TableDataRow[] = [
    {
        id: 1,
        application: 'AppKit Upgrader',
        tokenUsage: 0,
        estimationCost: 0,
    },
    {
        id: 2,
        application: 'AppKit Assist',
        tokenUsage: 0,
        estimationCost: 0,
    },
    {
        id: 3,
        application: 'Accessibility Checker (VS code)',
        tokenUsage: 0,
        estimationCost: 0,
    },
    {
        id: 4,
        application: 'Accessibility Checker (Site)',
        tokenUsage: 0,
        estimationCost: 0,
    },
    {
        id: 5,
        application: 'Total',
        tokenUsage: 0,
        estimationCost: 0,
    },
];
